import React, { useRef } from "react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WestIcon from "@mui/icons-material/West";
import CloseIcon from "@mui/icons-material/Close";
import "./DeliveryInvoiceEdit.css";
import { BatchDetailsMap, DeliveryInvoiceBatchDetails, ReturnSkuDetail, SalesReasonDetails } from "../../types/types";
import DeliveryInvoiceReasonSelectionParent from "./DeliveryInvoiceReasonSelectionParent";
import BatchListItem from "./BatchListItem";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { getBatchDetailsFromDb, getProductDetailsFromDb, setBatchDetailsInDb, fetchRestrictedSkuSet} from "../SalesReturn/SalesReturnService";
import { Autocomplete, TextField } from "@mui/material";
import { getDataFromLocalDb } from "../../services/dbService";
import { Product } from "../createSecondaryOrder/models";
import { getConfigData } from "../../services/configurationService";


const SkuDetailsAdd = ({isEditable}:{isEditable?:boolean}) => {

    const navigate = useNavigate();
    const location = useLocation();
    const [expandedBatch, setExpandedBatch] = useState<DeliveryInvoiceBatchDetails | null>(null);
    const [disabledUoms, setDisabledUoms] = useState<string[]>([]);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [editedDetails, setEditedDetails] = useState<DeliveryInvoiceBatchDetails[]>([]);
    const [restrictedSkuSet, setRestrictedSkuSet] = useState<Set<string>>(new Set());
    const selectedRowToEdit: ReturnSkuDetail | null = location.state?.rowData;
    const [skuCode, setSkuCode] = useState<string>(selectedRowToEdit?.batchCode ?? "");
    const [skuName, setSkuName] = useState<string>(selectedRowToEdit?.skuName ?? "");
    const [skuCodeOptions, setSkuCodeOptions] = useState<string[]>([]);
    const [skuNameOptions, setSkuNameOptions] = useState<string[]>([]);
    const otherUnitNameMap = useRef<any>()
    const [isUnsellableReturnRestrictedEnable, setIsUnsellableReturnRestrictedEnable] = useState<boolean>(false);
    const productDetails = useRef<{skuCode:string,skuDescription:string, batchIdDetails: any[]}[]>([]);
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
    const currency = ConfigDictionary?.currency;

    const setSkuDetails = async () => {
        const productData = await getProductDetailsFromDb();
        
        setSkuCodeOptions(
            Array.from(new Set(Object.keys(productData)))
        );
        setSkuNameOptions(
            Array.from(
                new Set(
                    Object.values(productData).map((product: any) => product.skuDescription)
                )
            )
        );

        productDetails.current = Object.values(productData).map((product:any)=>({skuCode:product.batchCode,skuDescription:product.skuDescription, batchIdDetails: product.batchIdDetails}));
    }

    useEffect(()=>{
        const fetchProducts = async () => {
            const productsData = await getDataFromLocalDb("Product");
            const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
            otherUnitNameMap.current = productsMap;
        };
        fetchProducts();
    },[])

    const setBatchDetailsInState = async()=>{
        const batchDetails = await getBatchDetailsFromDb();
        const selectedBatchData: { [key: string]: SalesReasonDetails[] } = (batchDetails && batchDetails[skuCode]) ?? {};
        let newBatchDetails: DeliveryInvoiceBatchDetails[] = Object.keys(selectedBatchData).map((batchId) => {
            return {
                batchId: batchId,
                caseQty: selectedBatchData[batchId].reduce((acc, current) => acc + current.caseQty, 0),
                otherQty: selectedBatchData[batchId].reduce((acc, current) => acc + current.otherQty, 0),
                pieceQty: selectedBatchData[batchId].reduce((acc, current) => acc + current.pieceQty, 0)
            };
        }) ?? [];
        newBatchDetails.forEach((batchData) => {
            batchData.reasonDetails = selectedBatchData[batchData.batchId];
        })
        
        const batchIdDetails = productDetails.current?.find(product=>product.skuCode == skuCode)?.batchIdDetails;
        if(batchIdDetails){
            batchIdDetails.forEach(batchIdDetail=>{
                const batch = newBatchDetails.find(batch=>batch.batchId == (batchIdDetail.batchId ?? "unassigned"));
                if(batch){
                    batch.mrp = batchIdDetail.mrp;
                    batch.packPtr = batchIdDetail.packPtr;
                    batch.expiryDate = batchIdDetail.expiryDate;
                }else{
                    newBatchDetails.push({batchId: batchIdDetail.batchId ?? "unassigned", caseQty: 0, otherQty: 0, pieceQty: 0, mrp: batchIdDetail.mrp, packPtr: batchIdDetail.packPtr, expiryDate: batchIdDetail.expiryDate});
                }
            })
            
        }else{
            const unassignedIndex = newBatchDetails.findIndex(details=>details.batchId=="unassigned");
            if(unassignedIndex>-1){
                const unassignedEntry = newBatchDetails.splice(unassignedIndex,1)[0];
                newBatchDetails.unshift(unassignedEntry);
            } else {
                newBatchDetails.splice(0, 0, { batchId: "unassigned", caseQty: 0, otherQty: 0, pieceQty: 0 });
            }
        }
        setEditedDetails(newBatchDetails);
        setExpandedBatch(null);
    }


    useEffect(() => {
        const setAllDetails = async()=>{    
            if (!selectedRowToEdit) {
                await setSkuDetails();
            }else{
                const productData = await getProductDetailsFromDb();
                productDetails.current = Object.values(productData).map((product:any)=>({skuCode:product.batchCode,skuDescription:product.skuDescription, batchIdDetails: product.batchIdDetails}));
            }
            setBatchDetailsInState();
            setExpandedIndex(null);
        }
        setAllDetails();

    }, [skuCode]);

    const handleOnClose = () => {
        navigate(-1);
    };

    const filterReasonDetails = (reasons: SalesReasonDetails[]) => {
        return reasons.filter(reasonDetail => {
            const qty = reasonDetail.caseQty + reasonDetail.otherQty + reasonDetail.pieceQty;
            if (qty > 0 && reasonDetail.reason) {
                return true;
            }
            if (qty > 0 && !reasonDetail.reason) {
                openPopup("Error", "Please add reasons for returns");
                throw Error("reason details not added");
            }
            return false;
        });
    }
    const handleOnSubmit = async () => {
        const newBatchDetails:BatchDetailsMap = (await getBatchDetailsFromDb()) ?? {};
        if(skuCode){
          const skuBatchDetails:{[key: string]: SalesReasonDetails[]} = {};
          editedDetails.forEach(batch=>{
            if(batch.batchId=="") {
                return;
            }
            const reasons = batch.reasonDetails??[];
            const filteredReasons = groupByReason(filterReasonDetails(reasons));
            if(filteredReasons.length>0){
                skuBatchDetails[batch.batchId] = filteredReasons;
            }
          })
          if(Object.keys(skuBatchDetails).length==0){
            delete newBatchDetails[skuCode];
          }else{
            newBatchDetails[skuCode] = skuBatchDetails;
          }
          await setBatchDetailsInDb(newBatchDetails);
        }
        handleOnClose()
    }
    function groupByReason(items: SalesReasonDetails[]): SalesReasonDetails[] {
        const groupedMap = items.reduce((acc, current) => {
            // If the reason doesn't exist in the map, initialize it
            if (!acc[current.reason]) {
                acc[current.reason] = {
                    reason: current.reason,
                    caseQty: 0,
                    pieceQty: 0,
                    otherQty: 0,
                    saleCondition: current.saleCondition
                };
            }

            // Sum the quantities
            acc[current.reason].caseQty += current.caseQty;
            acc[current.reason].pieceQty += current.pieceQty;
            acc[current.reason].otherQty += current.otherQty;

            return acc;
        }, {} as Record<string, SalesReasonDetails>);

        // Convert the map back to an array
        return Object.values(groupedMap);
    }

    const handleReset = () => {
        const batchIdDetails = productDetails.current?.find(product=>product.skuCode == skuCode)?.batchIdDetails;
        const newBatchDetails: DeliveryInvoiceBatchDetails[] = [];
        if(batchIdDetails){
            batchIdDetails.forEach(batchIdDetail=>{
                newBatchDetails.push({
                    batchId: batchIdDetail.batchId,
                    caseQty: 0,
                    otherQty: 0,
                    pieceQty: 0,
                    mrp: batchIdDetail.mrp,
                    packPtr: batchIdDetail.packPtr,
                    expiryDate: batchIdDetail.expiryDate
                });
            })
        }else{
            newBatchDetails.push({
                batchId: "unassigned",
                caseQty: 0,
                otherQty: 0,
                pieceQty: 0
            });
        }
        setEditedDetails(newBatchDetails);
        // selectedRowToEdit?.batchIds?.forEach(batchObject=>delete batchObject.reasonDetails);
        setExpandedBatch(null);
        // setStaticBatches(selectedRowToEdit?.batchIds || []);
    };

    const handleChangeExpandedBatch = async (index: number) => {
        if(index == expandedIndex){
            setExpandedIndex(null);
            setExpandedBatch(null);
        }else{
            try{
                const productDetails = await getProductDetailsFromDb();
                if(productDetails && productDetails[skuCode] && productDetails[skuCode].otherUnitToPieceQuantity == 0){
                    setDisabledUoms(["otherQty"]);
                }else{
                    setDisabledUoms([]);
                }
            }catch(error){
                console.log(error);
            }
            setExpandedIndex(index);
            setExpandedBatch(editedDetails[index]);
        }
    };

    const handleSkuCodeChange = (newSkuCode: string) => {
        setSkuCode(newSkuCode);
        const newSkuName = productDetails.current.find((product) => product.skuCode === newSkuCode)?.skuDescription;
        setSkuName(newSkuName ?? "");
    }
    const handleSkuNameChange = (newSkuName: string) => {
        setSkuName(newSkuName);
        const newSkuCode = productDetails.current.find((product) => product.skuDescription === newSkuName)?.skuCode;
        setSkuCode(newSkuCode ?? "");
    }

    const editBatchId = (batchId: string, index: number) => {
        const newBatchDetails = [...editedDetails];
        newBatchDetails[index].batchId = batchId;
        setEditedDetails(newBatchDetails);
    }


    useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      event.preventDefault();
      handleOnSubmit();
    } else if ((event.ctrlKey || event.metaKey) && (event.key === 'x' || event.key === 'z')) {
      event.preventDefault();
      handleReset();
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [skuCode, skuName, editedDetails, expandedBatch]);

useEffect(() => {
    const loadRestrictedSkus = async () => {
    try {
        const skuSet = await fetchRestrictedSkuSet();
        setRestrictedSkuSet(skuSet);
        const restrictedSkuConfig = await getConfigData(
                    "dms_configuration",
                    "restrictedSkuEnabled"
                  );
        const restrictedSku = restrictedSkuConfig[0]?.configValue ?? false;
        setIsUnsellableReturnRestrictedEnable(restrictedSku === true)
    } catch (e) {
        console.log("Failed to load restricted SKU set", e);
    }
    };
    loadRestrictedSkus();
}, []);

    return (
        <div className="edit-return-container">
            <header>
                <div>
                    <WestIcon style={{ cursor: "pointer" }} onClick={() => navigate(-1)} />
                    <p style={{ fontSize: "16px" }}> Add Product </p>
                </div>
                <CloseIcon style={{ cursor: "pointer" }} onClick={handleOnClose} />
            </header>
            <div className="add-return-model-container">
                <div className="sku-detail-header">
                    <div className="sku-detail-header-container">
                        <div className="sku-detail-header-label">SKU Code</div>
                        <Autocomplete
                            disableClearable
                            disabled={selectedRowToEdit?.batchCode ? true : false}
                            options={skuCodeOptions}
                            getOptionLabel={(option) => {
                                const isRestricted =
                                    isUnsellableReturnRestrictedEnable && restrictedSkuSet.has(option);
                                return isRestricted ? `${option} *` : option;
                            }}
                            value={skuCode}
                            onChange={(event, val) => handleSkuCodeChange(val ?? "")}
                            sx={{ width: "-webkit-fill-available" }}
                            renderOption={(props, option) => {
                                const isRestricted =
                                  isUnsellableReturnRestrictedEnable && restrictedSkuSet.has(option);
                                return (
                                  <li {...props}>
                                    {option}
                                    {isRestricted && (
                                      <span style={{ color: "red", marginLeft: 4 }}>*</span>
                                    )}
                                  </li>
                                );
                              }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="skuCode"
                                    variant="outlined"
                                    placeholder="Enter SKU Code"
                                    size="small"
                                    sx={customMuiInputStyle}
                                    required
                                />
                            )}
                        />
                    </div>

                    <div className="sku-detail-header-container">
                        <div className="sku-detail-header-label">SKU Name</div>
                        <Autocomplete
                            disableClearable
                            disabled={selectedRowToEdit?.batchCode ? true : false}
                            options={skuNameOptions}
                            getOptionLabel={(option) => {
                                const skuCode = productDetails.current
                                  .find(p => p.skuDescription === option)?.skuCode;
                                const isRestricted =
                                  skuCode && isUnsellableReturnRestrictedEnable && restrictedSkuSet.has(skuCode);
                                return isRestricted ? `${option} *` : option; 
                              }}                           
                               value={skuName}
                            onChange={(event, val) => handleSkuNameChange(val ?? "")}
                            sx={{ width: "-webkit-fill-available" }}
                            renderOption={(props, option) => {
                                const skuCode = productDetails.current
                                  .find(p => p.skuDescription === option)?.skuCode;
                                const isRestricted =
                                  skuCode && isUnsellableReturnRestrictedEnable && restrictedSkuSet.has(skuCode);
                                return (
                                  <li {...props}>
                                    {option}
                                    {isRestricted && (
                                      <span style={{ color: "red", marginLeft: 4 }}>*</span>
                                    )}
                                  </li>
                                );
                              }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="skuCode"
                                    variant="outlined"
                                    placeholder="Enter SKU Name"
                                    size="small"
                                    sx={customMuiInputStyle}
                                    required
                                />
                            )}
                        />
                    </div>
                </div>
                {isUnsellableReturnRestrictedEnable && restrictedSkuSet.has(skuCode) && (
                    <div className = "sku-detail-unselable-warning"  style={{
                        margin: "0px 20px -15px 120px",
        
                    }}>
                        *This product cannot be returned as “Unsaleable” as per company’s policy.
                    </div>
                )}
                {skuCode ? <div className="sku-detail-body">
                    <div className="sku-detail-batch-container">
                        <div className="sku-detail-batch-id-text">
                            Batch ID
                        </div>
                        <div className="sku-detail-add-left">
                            <div className="batches-list-container-delivery">
                                <div className="batch-list-heading">
                                    <span>Select a batch ID to add damage quantity</span>
                                </div>
                                <div className="batches-list-delivery">
                                    {editedDetails.map((batch, index) => {
                                        console.log("render");
                                        const hasQuantities = batch.caseQty!==0 || batch.pieceQty!==0 || batch.otherQty!==0;
                                        const hasReasonsWithQuantities = hasQuantities &&
                                            batch.reasonDetails &&
                                            batch.reasonDetails.length > 0 &&
                                            batch.reasonDetails.some(reason =>
                                                reason.reason &&
                                                reason.reason.trim() !== '' &&
                                                (reason.caseQty > 0 || reason.pieceQty > 0 || reason.otherQty > 0)
                                            );
                                        return (
                                            <>
                                                <BatchListItem
                                                    key={index}
                                                    // editBatchId={index!=0 && isEditable ? (batchId: string)=>{editBatchId(batchId,index)} : undefined}
                                                    batch={batch}
                                                    isSelected={expandedIndex === index}
                                                    onSelect={() => handleChangeExpandedBatch(index)}
                                                    isUpdated={hasReasonsWithQuantities}
                                                    otherUnitName= {otherUnitNameMap.current[skuCode]?.otherUnitName}

                                                />
                                            </>
                                        );
                                    })}
                                    {/* {allowBatchId && <div className="add-batch-delivery" onClick={() => handleAddBatch()}>
                                        + Add Batch Id
                                    </div>} */}
                                </div>
                            </div>
                        </div>
                    </div>
                    {expandedBatch && (
                        <div className="edit-right-delivery">
                            <div className="edit-right-delivery-top">
                                <div className="edit-right-delivery-top-header">
                                    <div style={{display: "flex", gap: "16px"}}>
                                        <p className="batch-id-nos-delivery">{expandedBatch.batchId}</p>
                                        {expandedBatch.mrp && <p className="batch-id-nos-delivery-mrp">(MRP:  {currency} {expandedBatch.mrp})</p>}
                                    </div>
                                    {expandedBatch.expiryDate && <p className="batch-id-nos-delivery-expiry-date">Expiry Date: {expandedBatch.expiryDate}</p>}
                                </div>
                                <div className="batch-ids-container-delivery">
                                    <div className="reasons-parent-delivery">
                                        <DeliveryInvoiceReasonSelectionParent isRestrictedSku={isUnsellableReturnRestrictedEnable ? restrictedSkuSet.has(skuCode): false} otherUnitName= {otherUnitNameMap.current[skuCode]?.otherUnitName} disabledUoms={disabledUoms} batchData={expandedBatch} ignoreValidation={true} updateParent={() => {
                                            setEditedDetails([...editedDetails]);
                                            }
                                        }/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div> : <></>}
            </div>
            <footer>
                <button className="button-reset-delivery" onClick={handleReset}>
                    <span className="button-reset-delivery-text">Reset</span>
                </button>
                <button className="button-save-delivery" onClick={() => { handleOnSubmit(); }}>
                    <span className="button-save-delivery-text" >Submit</span>
                </button>
            </footer>
        </div>
    );
};

const customMuiInputStyle = {
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: "#EAEAEA",
        },
        "&:hover fieldset": {
            borderColor: "#B9B9B9",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#1C7EA0",
        },
    },
    "& .MuiInputLabel-root": {
        color: "#B5B5B5",
        fontSize: "13px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#1C7EA0",
    },
};

export default SkuDetailsAdd;
